"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useState, useRef, useLayoutEffect, useMemo } from "react";
import { useMotionValue, useSpring, motion, AnimatePresence } from "motion/react";
import { usePathname } from "next/navigation";
import { cva, type VariantProps } from "class-variance-authority";
import type { LinkData, SubMenu } from "@/types";
import { Icon } from "@/components/ui/Icon";
import { Link } from "@/components/ui/Link";
import { MenuSectionIndicator, SubMenuSection } from "@/types/menu";
export const menuItemVariants = cva("inline-flex text-2xl px-sm w-full items-center justify-between overflow-x-hidden outline-offset-0 focus-visible:outline-2 focus-visible:outline-offset-2 text-white px-0 lg:px-md h-full rounded-full hover:text-white cursor-pointer whitespace-nowrap transition-all duration-200 outline-offset-0 focus-visible:outline-2 overflow-y-hidden", {
  variants: {
    variant: {
      dark: "aria-[current=page]:bg-purple-dark md:hover:bg-transparent md:aria-[current=page]:bg-transparent outline-yellow",
      light: "aria-[current=page]:bg-black/40 md:hover:bg-transparent md:aria-[current=page]:bg-transparent outline-yellow"
    },
    type: {
      logo: "w-32",
      link: "",
      submenu: ""
    }
  },
  defaultVariants: {
    variant: "dark"
  }
});
export const collapseButtonVariants = cva("flex-none px-sm text-white outline-offset-0 focus-visible:outline-2 focus-visible:outline-offset-2 rounded-full md:hover:bg-transparent outline-yellow");
const pillBgVariants: Record<string, string> = {
  dark: "lg:bg-purple-dark",
  light: "lg:bg-black"
};
export interface MobileSubMenuProps {
  data: SubMenu | LinkData;
  items: SubMenuSection[];
  variant?: VariantProps<typeof menuItemVariants>["variant"];
  isOpen: boolean;
  onToggle: () => void;
  isActive: boolean;
}
export const MobileSubMenu: React.FC<MobileSubMenuProps> = t0 => {
  const $ = _c(34);
  const {
    data,
    items,
    variant,
    isOpen,
    onToggle,
    isActive
  } = t0;
  const pathname = usePathname();
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const itemsWithoutPill = items.filter(_temp);
  let t1;
  if ($[0] !== items) {
    const itemsWithPill = items.filter(_temp2);
    t1 = itemsWithPill.flatMap(_temp3);
    $[0] = items;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const flatPillLinks = t1;
  const trackRef = useRef(null);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const itemRefs = useRef(t2);
  const isFirstPositionSet = useRef(false);
  const topMV = useMotionValue(0);
  const heightMV = useMotionValue(0);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      stiffness: 500,
      damping: 38
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const pillTop = useSpring(topMV, t3);
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      stiffness: 500,
      damping: 38
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const pillHeight = useSpring(heightMV, t4);
  let t5;
  if ($[5] !== flatPillLinks || $[6] !== pathname) {
    let t6;
    if ($[8] !== pathname) {
      t6 = link => pathname === link.href;
      $[8] = pathname;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    t5 = flatPillLinks.findIndex(t6);
    $[5] = flatPillLinks;
    $[6] = pathname;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const activeIndex = t5;
  const targetIndex = hoveredIndex ?? activeIndex;
  let t6;
  let t7;
  if ($[10] !== heightMV || $[11] !== pillHeight || $[12] !== pillTop || $[13] !== targetIndex || $[14] !== topMV) {
    t6 = () => {
      if (targetIndex < 0) {
        return;
      }
      const el = itemRefs.current[targetIndex];
      const track = trackRef.current;
      if (!el || !track) {
        return;
      }
      const top = el.getBoundingClientRect().top - track.getBoundingClientRect().top;
      const height = el.getBoundingClientRect().height;
      if (!isFirstPositionSet.current) {
        pillTop.jump(top);
        pillHeight.jump(height);
        isFirstPositionSet.current = true;
      } else {
        topMV.set(top);
        heightMV.set(height);
      }
    };
    t7 = [targetIndex, topMV, heightMV, pillTop, pillHeight];
    $[10] = heightMV;
    $[11] = pillHeight;
    $[12] = pillTop;
    $[13] = targetIndex;
    $[14] = topMV;
    $[15] = t6;
    $[16] = t7;
  } else {
    t6 = $[15];
    t7 = $[16];
  }
  useLayoutEffect(t6, t7);
  let t8;
  let t9;
  if ($[17] !== isOpen) {
    t8 = () => {
      if (!isOpen) {
        isFirstPositionSet.current = false;
      }
    };
    t9 = [isOpen];
    $[17] = isOpen;
    $[18] = t8;
    $[19] = t9;
  } else {
    t8 = $[18];
    t9 = $[19];
  }
  React.useEffect(t8, t9);
  const t10 = "flex flex-col";
  let t11;
  if ($[20] !== data.href || $[21] !== data.label || $[22] !== isActive || $[23] !== isOpen || $[24] !== items.length || $[25] !== onToggle || $[26] !== variant) {
    t11 = !!data.href ? <div className="flex"><Link label={data.label} href={data.href} isActive={isActive} className={menuItemVariants({
        variant
      })} />{items.length > 0 && <button type="button" onClick={onToggle} className={collapseButtonVariants()}><Icon name="chevron-down" size={20} className={`flex-none transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} /></button>}</div> : <button type="button" onClick={onToggle} aria-current={isActive ? "page" : undefined} className={menuItemVariants({
      variant
    })}><span>{data.label}</span><Icon name="chevron-down" size={20} className={`flex-none transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} /></button>;
    $[20] = data.href;
    $[21] = data.label;
    $[22] = isActive;
    $[23] = isOpen;
    $[24] = items.length;
    $[25] = onToggle;
    $[26] = variant;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const T0 = AnimatePresence;
  const t12 = isOpen && <motion.div initial={{
    height: 0
  }} animate={{
    height: "auto"
  }} exit={{
    height: 0
  }} transition={{
    type: "spring",
    stiffness: 500,
    damping: 38
  }} className="overflow-hidden"><div className={`flex flex-col gap-sm py-sm rounded-lg ${pillBgVariants[variant ?? "dark"]}`}>{itemsWithoutPill.map(_temp4)}{flatPillLinks.length > 0 && <div className="flex flex-row gap-sm items-stretch text-white pl-0 pr-sm" onMouseLeave={() => setHoveredIndex(null)} onBlur={() => setHoveredIndex(null)}><div ref={trackRef} className="relative w-0.5 flex-none bg-white/30 rounded-full"><motion.div className="absolute w-full bg-white rounded-full" style={{
            top: pillTop,
            height: pillHeight
          }} animate={{
            opacity: targetIndex >= 0 ? 1 : 0
          }} transition={{
            type: "spring",
            stiffness: 500,
            damping: 38
          }} /></div><ul className="flex flex-col gap-sm">{flatPillLinks.map((link_1, index) => <li key={index} ref={el_0 => {
            itemRefs.current[index] = el_0;
          }} onMouseEnter={() => setHoveredIndex(index)} onFocus={() => setHoveredIndex(index)} className="my-0"><div aria-hidden="true" className="flex font-medium h-0 overflow-hidden invisible pointer-events-none select-none items-center gap-xs">{link_1.icon && <Icon name={link_1.icon} size={16} />}<span>{link_1.label}</span></div><Link {...link_1} color="text" className={"hover:font-medium focus-visible:font-medium outline-yellow" + (index === activeIndex ? " font-medium" : "")} /></li>)}</ul></div>}</div></motion.div>;
  let t13;
  if ($[28] !== T0 || $[29] !== t12) {
    t13 = <T0>{t12}</T0>;
    $[28] = T0;
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t11 || $[32] !== t13) {
    t14 = <li className={t10}>{t11}{t13}</li>;
    $[31] = t11;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  return t14;
};
function _temp(s) {
  return (s.indicator ?? MenuSectionIndicator.Pill) === MenuSectionIndicator.None;
}
function _temp2(s_0) {
  return (s_0.indicator ?? MenuSectionIndicator.Pill) !== MenuSectionIndicator.None;
}
function _temp3(s_1) {
  return s_1.links;
}
function _temp4(section, si) {
  return section.links.map((link_0, li) => <Link key={`none-${si}-${li}`} {...link_0} color="text" className="text-white px-0 mx-sm hover:font-medium focus-visible:font-medium outline-yellow" />);
}